import React, { useEffect, useRef, useState } from "react";
import { Button, ButtonGroup, useDisclosure, useToast } from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faLayerGroup,
    faBookmark,
} from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import InputDialog from "../../../common/components/InputDialog";
import { contentActions, contentState } from "../../../store/slices/content";
import Header from "./ContentHeader";
import NoteContentForm, { NoteDataInput } from "./NoteContentForm";
import { ContentType } from "../../../common/types";
import WebContentForm, { WebDataInput } from "./WebContentForm";

const ContentInputDialog = ({
    saveContent,
    isOpen,
    onClose,
    inputForm,
}: {
    isOpen: boolean;
    onClose: () => void;
    saveContent: () => void;
    inputForm: React.ReactElement;
}) => {
    const { isSaving, hasErrorSaving } = useSelector(contentState);
    const [hasSaved, setHasSaved] = useState(false);
    const toast = useToast();

    const onSave = () => {
        setHasSaved(true);
        saveContent();
    };

    useEffect(() => {
        if (hasSaved && !isSaving) {
            setHasSaved(false);
            if (hasErrorSaving) {
                toast({
                    title: "No se puede guardar el Contenido.",
                    description:
                        "Por favor intenta de nuevo",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            } else {
                toast({
                    title: "Contenido guardado.",
                    description: "Se ha guardado de forma exitosa",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
                onClose();
            }
        }
    }, [isSaving, hasErrorSaving, hasSaved]);

    return (
        <InputDialog
            body={inputForm}
            isOpen={isOpen}
            onClose={onClose}
            header={
                <Header title="Contenido" onSave={onSave} isSaving={isSaving} />
            }
            isFullSize={true}
        />
    );
};

const CreateContentMenu = ({ subjectId }: { subjectId: string }) => {
    const noteDataRef = useRef<NoteDataInput>({ title: "", content: "" });
    const webDataRef = useRef<WebDataInput>({
        title: "",
        content: "",
        url: "",
    });
    const [contentType, setContentType] = useState<ContentType>("text");
    const [inputForm, setInputForm] = useState<React.ReactElement>(
        <NoteContentForm dataRef={noteDataRef} />
    );
    const { onOpen, isOpen, onClose } = useDisclosure();
    const dispatch = useDispatch();

    const saveContent = () => {
        if (contentType === "text") {
            dispatch(
                contentActions.postContent({
                    ...noteDataRef.current,
                    contentType,
                    subject: subjectId,
                })
            );
        } else if (contentType === "website") {
            dispatch(
                contentActions.postContent({
                    ...webDataRef.current,
                    contentType,
                    subject: subjectId,
                })
            );
        }
    };

    return (
        <>
            <ContentInputDialog
                isOpen={isOpen}
                onClose={onClose}
                saveContent={saveContent}
                inputForm={inputForm}
            />
            <ButtonGroup variant="outline" spacing="3">
                <Button
                    colorScheme="green"
                    leftIcon={<FontAwesomeIcon icon={faLayerGroup} />}
                    onClick={() => {
                        setInputForm(
                            <NoteContentForm
                                dataRef={noteDataRef}
                            />
                        );
                        setContentType("text");
                        onOpen();
                    }}
                >
                    Crear Nota
                </Button>
                <Button
                    colorScheme="green"
                    leftIcon={<FontAwesomeIcon icon={faBookmark} />}
                    onClick={() => {
                        setInputForm(
                            <WebContentForm dataRef={webDataRef} />
                        );
                        setContentType("website");
                        onOpen();
                    }}
                >
                    Guardar Enlace
                </Button>
            </ButtonGroup>
        </>
    );
};

export default CreateContentMenu;
